import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import "./user-permission.css";

import HintModal from "../common/hint-modal";
import filterUsersByName from "../common/filter-users-by-name";
import UserChatBubble from "../../Forms/FirmUserBtn";
import avatarImage from "../../../assets/images/avatar.svg";
import ChatSendingPopup from "../../Forms/common/ChatSendingPopup";
import useCheckAttorneyUser from "../user-accounts/hooks/useCheckAttorneyUser";
import { getToken } from "../../../Utils/helper";
import { applyPermissionPatchForUser } from "../../../Redux/authPermissions/authPermissionsSlice";

import useUpdateUserPermission from "./hooks/useUpdateUserPermission";
import useGetUserPermission from "./hooks/useGetUserPermission";

const UserPermissions = ({ searchTerm = "" }) => {
  const dispatch = useDispatch();
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const token = getToken();
  const points = [
    "Edit Case Workers: User can edit firm user names and email addresses.",
    "View All Cases: User can view all cases in the firm.",
    "View Only Team: User can only access cases they are attached to but still view all cases in search results.",
    "Email Clients: User can email clients from client page.",
    "Chat with Client: User can chat with clients through client page.",
    "Text Client: User can send texts to client from client page.",
    "Edit Firm Branding: User can modify the look of the program through the Firm Branding settings panel.",
    "Edit User Avatars: User can modify the avatars for each user in the firm.",
    "Edit Client Notes: User can edit notes on a case file.",
    "Edit S.O.L.: User can edit the statute of limitations on a case file.",
    "Edit Lit. Events: User can edit litigation events on a case file.",
    "View Non-Case Docs: User can view case documents not associated with a case file in firm management.",
    "Edit Doc Templates: User can modify document templates accessible via the generate document button.",
    "Edit Objections: User can edit the objections that are selectable to apply to discovery responses.",
    "Add Case To Client: User can add a new case to an existing client. (Waleed- edit the title from + Case or Client.",
  ];

  const firmSettingFields = [
    { key: "assign_firm_user", label: "Edit Case Workers" },
    { key: "view_all_cases", label: "View All Cases" },
    { key: "view_only_team_cases", label: "View Only Team" },
    { key: "communicate_via_email", label: "Email Clients" },
    { key: "communicate_via_chat", label: "Chat with Client" },
    { key: "communicate_via_text", label: "Text Client" },
    { key: "modify_firm_branding", label: "Edit Firm Branding" },
    { key: "modify_firm_avatar", label: "Edit User Avatars" },
    { key: "edit_notes", label: "Edit Client Notes" },
    { key: "edit_statute_of_limitations", label: "Edit S.O.L." },
    { key: "edit_litigation_events", label: "Edit Lit. Events" },
    { key: "receive_non_client_scanned_email", label: "View Non-Case Docs" },
    { key: "edit_letter_templates", label: "Edit Doc Templates" },
    { key: "edit_discovery_objections", label: "Edit Objections" },
    { key: "add_new_client", label: "+ Case or Client" },
    { key: "edit_todo_permission", label: "Delete or Edit tasks" },
  ];

  const permissionHeaderData = ["Accounting", "Admin", "Intake", "Active", "Reset Password"];

  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);

  const { data: getUserPermission, _loading, _error, refetch } = useGetUserPermission();
  const { updateFirmSetting } = useUpdateUserPermission();
  const { data: userExists } = useCheckAttorneyUser();
  const [showHints, setShowHints] = useState(false);

  const [chatData, _setChatData] = useState(null);
  const [chatModal, setChatModal] = useState(false);
  // const openChatModel = (event, data) => {
  //   event?.stopPropagation();
  //   setChatData(data);
  //   setChatModal(!chatModal);
  // };

  const handleHintToggle = () => {
    setShowHints(!showHints);
  };

  const [userPermissionState, setUserPermissionState] = useState([]);
  const filteredUsers = useMemo(
    () => filterUsersByName(userPermissionState, searchTerm),
    [userPermissionState, searchTerm]
  );
  useEffect(() => {
    if (getUserPermission) {
      setUserPermissionState(getUserPermission);
    }
  }, [getUserPermission]);

  const handleCheckboxChange = async (userId, settingKey, currentValue, fullFirmSettings) => {
    if (!fullFirmSettings) {
      return;
    }

    const updatedPermissions = getUserPermission?.map((user) => {
      if (user?.user?.id === userId) {
        return {
          ...user,
          for_firmsetting: {
            ...user.for_firmsetting,
            [settingKey]: !currentValue,
          },
        };
      }
      return user;
    });

    setUserPermissionState(updatedPermissions);

    const updatedSettings = {
      ...fullFirmSettings,
      [settingKey]: !currentValue,
    };

    const result = await updateFirmSetting(userId, updatedSettings);
    refetch();
    if (result) {
      dispatch(
        applyPermissionPatchForUser({
          userId,
          permissions: { [settingKey]: !currentValue },
        })
      );
    } else {
      const revertedPermissions = getUserPermission?.map((user) => {
        if (user?.user?.id === userId) {
          return {
            ...user,
            for_firmsetting: {
              ...user.for_firmsetting,
              [settingKey]: currentValue,
            },
          };
        }
        return user;
      });
      setUserPermissionState(revertedPermissions);
    }
  };

  // --- accounting/admin/intake/active/reset-password logic (moved from UserAccounts) ---
  const handleAccessPermissionChange = async (userId, permissionKey, value) => {
    const userIndex = userPermissionState.findIndex((user) => user.user.id === userId);
    const user = userPermissionState[userIndex];

    const previousValue = user[permissionKey];

    let newValue;
    if (
      permissionKey === "accounting_permission" ||
      permissionKey === "accounting_admin_permission" ||
      permissionKey === "reset_password_permission" ||
      permissionKey === "isIntake" ||
      permissionKey === "is_active"
    ) {
      newValue = value ? true : false;
    } else {
      newValue = value;
    }
    // console.log(newValue);
    // console.log(userId);
    // console.log(permissionKey);
    // console.log(value);
    const updatedPermissions = [...userPermissionState];

    if (permissionKey === "isIntake") {
      updatedPermissions[userIndex] = {
        ...user,
        isIntake: newValue === true,
      };
    } else {
      updatedPermissions[userIndex] = {
        ...user,
        [permissionKey]: newValue,
      };
    }
    // console.log(updatedPermissions);
    setUserPermissionState(updatedPermissions);

    try {
      const payload = {
        user_id: userId,
        is_accounting:
          permissionKey === "accounting_permission"
            ? newValue
            : user.accounting_permission === true
              ? true
              : false,
        is_admin:
          permissionKey === "accounting_admin_permission"
            ? newValue
            : user.accounting_admin_permission === true
              ? true
              : false,
        is_intake: permissionKey === "isIntake" ? newValue : user.isIntake === true ? true : false,
        is_active:
          permissionKey === "is_active" ? newValue : user.is_active === true ? true : false,
        reset_password:
          permissionKey === "reset_password_permission"
            ? newValue
            : user.reset_password_permission === true
              ? true
              : false,
      };

      await axios.post(`${origin}/api/firmsetting-page/update-useraccount-permissions/`, payload, {
        headers: {
          Authorization: token,
        },
      });
      dispatch(
        applyPermissionPatchForUser({
          userId,
          permissions: { [permissionKey]: newValue },
        })
      );
    } catch (error) {
      console.error("Error updating permissions:", error);
      const revertedPermissions = [...userPermissionState];
      revertedPermissions[userIndex] = {
        ...user,
        [permissionKey]: previousValue,
      };
      setUserPermissionState(revertedPermissions);
    }
  };

  return (
    <div className="tab-pane fade firm-settings-user-perms-fs active show">
      <div className="w-100">
        <div className="row mx-auto">
          <table
            className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table"
            id="treatment-summary-table"
            style={{ marginTop: "0px" }}
          >
            <thead>
              <tr id="user-perm">
                <th
                  className="text-center  text-uppercase"
                  style={{
                    backgroundColor: "white",
                    width: "25px",
                    maxWidth: "25px",
                    minWidth: "25px",
                    verticalAlign: "bottom",
                  }}
                />
                <th
                  className="text-center p-0 td-autosize user-info-class text-uppercase"
                  style={{ backgroundColor: "white", verticalAlign: "bottom" }}
                >
                  <div>
                    <span style={{ fontWeight: "600" }}>Firm User</span>
                  </div>
                </th>
                <th
                  className="text-center  text-uppercase"
                  style={{
                    backgroundColor: "white",
                    width: "15px",
                    maxWidth: "15px",
                    minWidth: "15px",
                    verticalAlign: "bottom",
                  }}
                />
                {userExists?.attorney_user &&
                  permissionHeaderData.map((header, index) => (
                    <>
                      <th
                        key={`access-${index}`}
                        style={{
                          textAlign: "start",
                          backgroundColor: "white",
                          verticalAlign: "bottom",
                          fontWeight: "600",
                        }}
                        className="text-uppercase"
                      >
                        <div className="row-rotation-firm-settings">
                          <span>{header}</span>
                        </div>
                      </th>
                      <th style={{ backgroundColor: "white" }} />
                    </>
                  ))}
                {firmSettingFields.map(({ key, label }) => (
                  <>
                    <th
                      style={{
                        textAlign: "start",
                        backgroundColor: "white",
                        verticalAlign: "bottom",
                        fontWeight: "600",
                      }}
                      key={key}
                      className="text-uppercase"
                    >
                      <div className="row-rotation-firm-settings">
                        <span>{label}</span>
                      </div>
                    </th>
                    <th style={{ backgroundColor: "white" }} />
                  </>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 &&
                filteredUsers.map((userData, index) => (
                  <tr key={userData.id} style={{ height: "35px" }}>
                    <td
                      className=" text-center"
                      style={{ color: "var(--primary-25)", fontWeight: "600" }}
                    >
                      {index + 1}
                    </td>

                    <td className="td-autosize text-center">
                      <span className="ps-2 d-flex align-items-center">
                        <UserChatBubble
                          first_name={userData?.user?.first_name}
                          last_name={userData?.user?.last_name}
                          profilePic={userData?.profile_pic_29p ?? ""}
                          // onClick={(e) => openChatModel(e, userData)}
                          hideIcon={true}
                          isActive={userOnlineStatus?.[userData?.user?.id]?.isOnline ?? false}
                          is_active={userData?.user?.is_active}
                        />
                      </span>
                    </td>
                    <td />

                    {userExists?.attorney_user && (
                      <>
                        <td className="td-autosize text-center">
                          <input
                            type="checkbox"
                            checked={userData?.accounting_permission === true}
                            onChange={(e) =>
                              handleAccessPermissionChange(
                                userData.user.id,
                                "accounting_permission",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td />
                        <td className="td-autosize text-center">
                          <input
                            type="checkbox"
                            checked={userData?.accounting_admin_permission === true}
                            onChange={(e) =>
                              handleAccessPermissionChange(
                                userData.user.id,
                                "accounting_admin_permission",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td />
                        <td className="td-autosize text-center">
                          <input
                            type="checkbox"
                            checked={userData?.isIntake === true}
                            onChange={(e) =>
                              handleAccessPermissionChange(
                                userData.user.id,
                                "isIntake",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td />
                        <td className="td-autosize text-center">
                          <input
                            type="checkbox"
                            checked={userData?.is_active === true}
                            onChange={(e) =>
                              handleAccessPermissionChange(
                                userData.user.id,
                                "is_active",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td />
                        <td className="td-autosize text-center">
                          <input
                            type="checkbox"
                            checked={userData?.reset_password_permission === true}
                            onChange={(e) =>
                              handleAccessPermissionChange(
                                userData.user.id,
                                "reset_password_permission",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td />
                      </>
                    )}
                    {firmSettingFields.map(({ key: settingKey }) => {
                      const firmSettings = userData?.for_firmsetting;

                      return (
                        <>
                          <td className="td-autosize text-center" key={settingKey}>
                            <input
                              type="checkbox"
                              checked={firmSettings?.[settingKey] === true}
                              disabled={!firmSettings}
                              onChange={() =>
                                handleCheckboxChange(
                                  userData.user?.id,
                                  settingKey,
                                  firmSettings?.[settingKey],
                                  firmSettings
                                )
                              }
                            />
                          </td>
                          <td />
                        </>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {showHints && <HintModal show={showHints} handleClose={handleHintToggle} points={points} />}
      {chatModal && (
        <ChatSendingPopup
          data={chatData}
          show={chatModal}
          handleClose={() => setChatModal(false)}
          first_name={chatData?.user?.first_name}
          last_name={chatData?.user?.last_name}
          userId={chatData?.user?.id}
          profilePic={chatData?.user?.profile_pic_29p || avatarImage}
          is_active={chatData?.user?.is_active}
        />
      )}
    </div>
  );
};

export default UserPermissions;
